import { useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Nav from '../components/Nav';
import SiteFooter from '../components/SiteFooter';
import useReveal, { Scrubbed } from '../useReveal';
import { pickPigs } from '../pigBank';

const P = process.env.PUBLIC_URL;

const MARQUEE = ['Cozy', 'Playful', 'Pixel-perfect', 'Pick-up-and-play', 'Made with heart'];

// Pigs are pure decoration — no cards, no names. They drift in the page
// gutters as you scroll.
const DecorPig = ({ src, anim = 'pig-float', style }) => (
  <img src={src} alt="" aria-hidden="true" className={`pixel decor-pig ${anim}`} style={style} />
);

export default function Home() {
  const scope = useRef(null);
  useReveal(scope);

  // Fill the five decoration slots with a random, distinct draw from the pig
  // bank — recomputed once per mount, so each page load shows a fresh cast.
  const pigs = useMemo(() => pickPigs(5), []);

  return (
    <div className="theme-dark grain" ref={scope}>
      <div className="site">
        <Nav
          items={[
            { label: 'Pot Belly Pig Studios', to: '/', active: true },
            { label: 'Slope Sliders', to: '/slope-sliders' },
          ]}
        />

        {/* ATTENTION */}
        <section className="hero hero-center container">
          <div
            className="hero-glow"
            style={{ background: 'radial-gradient(38% 34% at 50% 38%, rgba(124,92,255,0.45), transparent 70%)' }}
          />
          <img
            src={`${P}/pig-icon.png`}
            alt="Pot Belly Pig Studios mascot"
            className="pig-bob"
            style={{ width: 'clamp(150px, 22vw, 270px)', filter: 'drop-shadow(0 30px 60px rgba(76,40,170,0.55))' }}
          />
          <span className="eyebrow" style={{ marginTop: '2rem' }}>Independent game studio</span>
          <h1 className="h1">Cozy little games, made with a big heart.</h1>
          <p className="lede">
            Pot Belly Pig Studios crafts friendly, pick-up-and-play mobile games with character to
            spare. First out of the pen: an endless icy descent starring one very determined penguin.
          </p>
          <div className="cta-row" style={{ justifyContent: 'center' }}>
            <Link to="/slope-sliders" className="btn btn-primary">Play Slope Sliders</Link>
            <Link to="/slope-sliders#wiki" className="btn btn-ghost">Read the guide</Link>
          </div>
        </section>

        {/* INTEREST — gapless bento (content only) */}
        <section className="section" style={{ position: 'relative' }}>
          <DecorPig src={pigs[0]} anim="pig-bob" style={{ left: 'clamp(0px,2vw,40px)', top: '6%' }} />
          <DecorPig src={pigs[1]} style={{ right: 'clamp(0px,2vw,40px)', top: '54%' }} />
          <div className="container">
            <div className="section-head" data-reveal>
              <h2 className="h2">Small games. Big personality.</h2>
              <p className="section-lede lede">
                Every release gets a hand-tuned feel and the kind of polish you only get when you
                actually love the thing you ship.
              </p>
            </div>

            <div className="bento bento-home" style={{ marginTop: 'clamp(2.5rem,5vw,4rem)' }}>
              <Link to="/slope-sliders" className="cell col-2 row-2" data-reveal style={{ minHeight: 0 }}>
                <div className="cell-media" data-img>
                  <img src={`${P}/slope-sliders-icon.png`} alt="Slope Sliders" />
                </div>
                <div className="cell-scrim" />
                <span className="eyebrow" style={{ color: '#fff', marginBottom: '0.6rem' }}>Now playing</span>
                <span className="cell-title display-accent" style={{ color: '#fff', fontSize: 'clamp(1.6rem,3vw,2.4rem)' }}>Slope Sliders</span>
                <span className="cell-sub" style={{ color: 'rgba(255,255,255,0.82)' }}>
                  An endless 3D runner down a mountain that never quits. Slide, dodge, boost, repeat.
                </span>
              </Link>

              <div className="cell col-2" data-reveal>
                <span className="cell-title" style={{ fontSize: 'clamp(1.2rem,2vw,1.7rem)', fontWeight: 600 }}>
                  We only build games we&rsquo;d happily lose an afternoon to.
                </span>
              </div>

              <Link to="/slope-sliders#wiki" className="cell" data-reveal>
                <span className="cell-title" style={{ fontSize: '1.15rem' }}>Player guide</span>
                <span className="cell-sub">Every system, explained &rarr;</span>
              </Link>

              <div className="cell" data-reveal>
                <span className="cell-title" style={{ fontSize: '1.15rem' }}>More on the way</span>
                <span className="cell-sub">New games are in the pen.</span>
              </div>
            </div>
          </div>
        </section>

        {/* DESIRE — scrub manifesto with inline pig (decoration) */}
        <section className="section" style={{ position: 'relative' }}>
          <DecorPig src={pigs[2]} style={{ left: 'clamp(0px,2vw,40px)', top: '8%' }} />
          <DecorPig src={pigs[3]} anim="pig-bob" style={{ right: 'clamp(0px,2vw,40px)', bottom: '4%' }} />
          <div className="container">
            <p className="statement scrub" data-reveal>
              <Scrubbed text="We put a little" />
              <span
                className="inline-pig"
                style={{
                  display: 'inline-block', width: '1.4em', height: '1.4em', verticalAlign: '-0.35em', margin: '0 0.2em',
                  backgroundImage: `url(${P}/pig-icon.png)`, backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundPosition: 'center',
                }}
                aria-hidden="true"
              />
              <Scrubbed text="into everything we make — and it shows." />
            </p>
          </div>
        </section>

        {/* DESIRE — text marquee */}
        <section className="section" style={{ position: 'relative', paddingBlock: 'clamp(3rem,7vw,6rem)' }}>
          <DecorPig src={pigs[4]} anim="pig-bob" style={{ right: 'clamp(0px,3vw,60px)', top: '-2%' }} />
          <div className="marquee">
            <div className="marquee-track">
              {[...MARQUEE, ...MARQUEE, ...MARQUEE].map((w, i) => (
                <span className="marquee-item" key={i}>{w}</span>
              ))}
            </div>
          </div>
        </section>

        {/* ACTION */}
        <SiteFooter ctaTitle="Ready to hit the slope?" ctaButton="Play Slope Sliders" ctaTo="/slope-sliders" />
      </div>
    </div>
  );
}
